import { webStorage } from 'sidecar-utility-library'

import { logError } from 'utils/monitoring/datadog'

/**
 * The session member-app (the Next.js half of the member experience) runs on.
 * Member-app keeps it in an HttpOnly cookie it writes and clears itself, so
 * this module only talks to its two routes, both same-origin through the MFE
 * proxy.
 *
 * The session is a projection of what the SPA has loaded: whenever that
 * changes, the whole thing is sent again and member-app replaces its cookie.
 */

const SET_SESSION_URL = '/api/auth/set-cookies'
const CLEAR_SESSION_URL = '/api/auth/logout'

// Writes run one at a time, in order. A clear doesn't wait behind them — the
// page is often about to unload — so it bumps `generation` instead: queued
// writes from before it are dropped, and one already in flight is followed by
// another clear in case it lands afterwards and puts the session back.
let queue = Promise.resolve()
let generation = 0
let pendingWrites = 0
let lastSent = null
// When `lastSent` last failed, or null. A failed write is sent again by a sync
// at least `RETRY_AFTER_MS` later, so a blip recovers without every action in
// between resending it.
let lastFailedAt = null
const RETRY_AFTER_MS = 5000
let syncedToken = null
let waiters = []
// The clear in flight; a clear requested while it runs joins it.
let clearing = null

const readJson = key => {
  try {
    return JSON.parse(webStorage.getItem(key) || 'null')
  } catch {
    return null
  }
}

/**
 * The payload member-app's `POST /api/auth/set-cookies` takes, or `null` until
 * the authed user for the stored token has loaded.
 *
 * `sh:member` must belong to the stored `sub`, so a profile left over from a
 * previous member is never paired with a new member's token.
 */
export const buildMemberAppSession = () => {
  const token = webStorage.getItem('token')
  if (!token) return null
  const member = readJson('sh:member')
  if (!member?.uuid || member.uuid !== webStorage.getItem('sub')) return null

  // Read once; both lists come off the same object.
  const accessMap = readJson('accountMemberAccessMap')

  return {
    member_token: token,
    member_uuid: member.uuid,
    account_uuid: webStorage.getItem('aud') || '',
    group_uuid: member.asoGroupDto?.uuid || '',
    insurance_filing_uuid: member.policy?.insuranceFilingUuid || '',
    policy_uuid: member.policy?.uuid || '',
    insurance_product: member.policy?.insuranceProduct || '',
    phi_uuid_list: accessMap?.PHI || [],
    // BASE is a separate list from PHI, not a subset of the same idea: BASE
    // covers "may see everything about this member, contact info included",
    // which is what the ID-card switcher gates on (the same list /idcard
    // filters with).
    //
    // Carrying it here lets member-app apply it on the server, so a hidden
    // member's name never reaches the browser. That is all it buys: this is
    // **not** a security boundary. The value starts in localStorage, which the
    // member can edit, and `/api/auth/set-cookies` only checks its shape — so a
    // member can put any uuid in this list. The real gate has to be the members
    // API scoping its response by caller (MX-5523); `phi_uuid_list` above has
    // always had the same property.
    base_uuid_list: accessMap?.BASE || [],
    member_zip: member.mailingAddress?.zip || '',
    member_city: member.mailingAddress?.city || '',
    member_state: member.mailingAddress?.state || '',
  }
}

/**
 * Whether member-app holds the session for `token`: written, with no write
 * still queued behind it.
 */
const isSessionSettled = token =>
  !!token && token === syncedToken && pendingWrites === 0

const settleWaiters = () => {
  waiters = waiters.filter(waiter => {
    if (!isSessionSettled(waiter.token)) return true
    waiter.resolve(true)
    return false
  })
}

const failWaiters = token => {
  waiters = waiters.filter(waiter => {
    if (waiter.token !== token) return true
    waiter.resolve(false)
    return false
  })
}

const send = async body => {
  try {
    const response = await fetch(SET_SESSION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
    if (!response.ok) {
      logError('Failed to set member-app session', { status: response.status })
    }
    return response.ok
  } catch (error) {
    logError('Failed to set member-app session', { error })
    return false
  }
}

const isRetryDue = () =>
  lastFailedAt !== null && Date.now() - lastFailedAt >= RETRY_AFTER_MS

/**
 * Sends the session to member-app unless it is the one last sent (or that one
 * failed and a retry is due). Resolves once member-app has answered.
 *
 * @param {Object} session - From `buildMemberAppSession`.
 */
export const syncMemberAppSession = session => {
  const body = JSON.stringify(session)
  if (body === lastSent && !isRetryDue()) return queue
  lastSent = body
  lastFailedAt = null

  const writeGeneration = generation
  pendingWrites += 1
  queue = queue.then(async () => {
    try {
      if (writeGeneration !== generation) return
      const ok = await send(body)
      if (writeGeneration !== generation) {
        // Awaited so a write queued behind this one can't land first and be
        // deleted by it.
        await deleteSession()
        return
      }
      if (ok) {
        syncedToken = session.member_token
      } else {
        if (lastSent === body) lastFailedAt = Date.now()
        // Member-app may no longer hold what was synced (a 413 deletes it), so
        // a wait holds until a write succeeds again.
        if (syncedToken === session.member_token) syncedToken = null
        failWaiters(session.member_token)
      }
    } finally {
      pendingWrites -= 1
      settleWaiters()
    }
  })
  return queue
}

/**
 * Resolves `true` once member-app's session for the stored token is settled
 * (see `isSessionSettled`), and `false` if writing it failed. Navigating to a
 * member-app page with no session would bounce straight back to /login.
 *
 * After `timeoutMs` it resolves with whether any session was written: one that
 * lags a queued write still beats sending the member to the dashboard.
 */
export const waitForMemberAppSession = (timeoutMs = 10000) => {
  const token = webStorage.getItem('token')
  if (isSessionSettled(token)) return Promise.resolve(true)

  return new Promise(resolve => {
    const waiter = { token, resolve }
    waiters.push(waiter)
    setTimeout(() => {
      if (!waiters.includes(waiter)) return
      waiters = waiters.filter(w => w !== waiter)
      resolve(!!token && token === syncedToken)
    }, timeoutMs)
  })
}

const deleteSession = () =>
  fetch(CLEAR_SESSION_URL, { method: 'DELETE', keepalive: true }).catch(error =>
    logError('Failed to clear member-app session', { error }),
  )

/**
 * Ends the member-app session. Safe to call when there isn't one. A call while
 * the previous clear is still in flight joins it rather than sending another.
 */
export const clearMemberAppSession = () => {
  generation += 1
  lastSent = null
  lastFailedAt = null
  syncedToken = null
  if (!clearing) {
    clearing = deleteSession().finally(() => {
      clearing = null
    })
  }
  return clearing
}
